import { useState, useEffect, useRef } from 'react';
import { Button, Input, useToast } from '@centraoneplus/ui';
import {
  Timer,
  Plus,
  Search,
  Edit,
  Trash2,
  Power,
  PowerOff,
  Play,
  ChevronDown,
  ChevronUp,
  X,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import workflowService, {
  ScheduledJobType,
  ScheduledJob,
  CreateScheduledJobDto,
  JobStats,
} from '@/services/workflow.service';
import JobConfigPanel, { getDefaultJobConfig, getMissingJobConfigFields } from './JobConfigPanel';
import { ThemedSelect } from '@components/common/ThemedSelect';

// Job type display labels. Kept complete (incl. SLA_CHECK) so existing jobs of
// any type still render a proper label on their card.
const JOB_TYPE_LABELS: Record<ScheduledJobType, string> = {
  [ScheduledJobType.AUTOMATION_RULE]: 'Automation Rule',
  [ScheduledJobType.N8N_WORKFLOW]: 'n8n Workflow',
  [ScheduledJobType.SLA_CHECK]: 'SLA Check',
  [ScheduledJobType.STALE_TICKET_CHECK]: 'Stale Ticket Check',
  [ScheduledJobType.REPORT_GENERATION]: 'Report Generation',
  [ScheduledJobType.CLEANUP]: 'Cleanup',
  [ScheduledJobType.CUSTOM]: 'Custom Webhook',
};

// Job types a user can create from the UI. SLA_CHECK is intentionally excluded:
// SLA is already monitored continuously by ticket-service (every 60s), so a
// manually scheduled SLA Check is redundant. Existing SLA_CHECK jobs still
// display (via JOB_TYPE_LABELS) and can be toggled/deleted — they just can't be
// created anymore.
const CREATABLE_JOB_TYPES: ScheduledJobType[] = [
  ScheduledJobType.AUTOMATION_RULE,
  ScheduledJobType.N8N_WORKFLOW,
  ScheduledJobType.STALE_TICKET_CHECK,
  ScheduledJobType.REPORT_GENERATION,
  ScheduledJobType.CLEANUP,
  ScheduledJobType.CUSTOM,
];

// Run status display
const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  running: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  skipped: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  pending: <Clock className="w-3 h-3" />,
  running: <Loader2 className="w-3 h-3 animate-spin" />,
  completed: <CheckCircle className="w-3 h-3" />,
  failed: <XCircle className="w-3 h-3" />,
  skipped: <AlertTriangle className="w-3 h-3" />,
};

// ---- Friendly schedule picker helpers ----
type ScheduleFrequency =
  | 'every_15_min'
  | 'every_30_min'
  | 'hourly'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'custom';

// Cron day-of-week: 0 = Sunday ... 6 = Saturday.
const DAY_OF_WEEK_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '0', label: 'Sunday' },
];

export type IntervalUnit = 'minutes' | 'hours';

export interface ScheduleState {
  frequency: ScheduleFrequency;
  hour: number;
  minute: number;
  dayOfWeek: string;
  dayOfMonth: number;
  // "Custom" means "run on a repeating interval". `intervalMinute` is the
  // minute-past-the-hour and only applies to the 'hours' unit — a `*/N` minutes
  // expression already fires at :00, :N, :2N… so a start minute is meaningless
  // there.
  intervalValue: number;
  intervalUnit: IntervalUnit;
  intervalMinute: number;
}

// Interval choices are restricted to divisors of 60 / 24 so the job fires at an
// even cadence. `*/45 * * * *` is valid cron but fires at :00 then :45 and
// resets — a 15-minute gap nobody asked for. Offering only clean divisors makes
// "every N" mean what the user reads it as.
export const INTERVAL_OPTIONS: Record<IntervalUnit, number[]> = {
  minutes: [1, 2, 3, 5, 10, 15, 20, 30],
  hours: [1, 2, 3, 4, 6, 8, 12],
};

// Default interval when switching units — the current value rarely exists in
// the other unit's list, and picking a fixed sane default beats a surprising
// numeric "nearest match" (30 minutes → 12 hours).
const DEFAULT_INTERVAL: Record<IntervalUnit, number> = { minutes: 15, hours: 1 };

const MINUTE_PAST_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

const isNum = (s: string) => /^\d+$/.test(s);
// `*/7` → 7, anything else → null.
const asStep = (s: string) => (/^\*\/\d+$/.test(s) ? Number(s.slice(2)) : null);
const pad2 = (n: number) => String(n).padStart(2, '0');

// Turn a cron string into friendly picker state. Interval expressions map onto
// the Custom interval builder; anything still unrecognized lands on Custom with
// safe defaults (SchedulePicker's didMount guard means the stored expression is
// NOT rewritten unless the user actually changes a control).
export function parseCron(cron: string): ScheduleState {
  const fallback: ScheduleState = {
    frequency: 'custom',
    hour: 9,
    minute: 0,
    dayOfWeek: '1',
    dayOfMonth: 1,
    intervalValue: 6,
    intervalUnit: 'hours',
    intervalMinute: 0,
  };
  const parts = (cron || '').trim().split(/\s+/);
  if (parts.length !== 5) return fallback;
  const [min, hr, dom, mon, dow] = parts;
  if (mon !== '*') return fallback;

  if (min === '*/15' && hr === '*' && dom === '*' && dow === '*') return { ...fallback, frequency: 'every_15_min' };
  if (min === '*/30' && hr === '*' && dom === '*' && dow === '*') return { ...fallback, frequency: 'every_30_min' };
  if (min === '0' && hr === '*' && dom === '*' && dow === '*') return { ...fallback, frequency: 'hourly', minute: 0 };
  if (isNum(min) && isNum(hr) && dom === '*' && dow === '*')
    return { ...fallback, frequency: 'daily', hour: Number(hr), minute: Number(min) };
  if (isNum(min) && isNum(hr) && dom === '*' && isNum(dow))
    return { ...fallback, frequency: 'weekly', hour: Number(hr), minute: Number(min), dayOfWeek: dow };
  if (isNum(min) && isNum(hr) && isNum(dom) && dow === '*')
    return { ...fallback, frequency: 'monthly', hour: Number(hr), minute: Number(min), dayOfMonth: Number(dom) };

  // Interval forms → the Custom interval builder.
  const minStep = asStep(min);
  if (minStep !== null && hr === '*' && dom === '*' && dow === '*')
    return { ...fallback, intervalUnit: 'minutes', intervalValue: minStep, intervalMinute: 0 };
  const hrStep = asStep(hr);
  if (hrStep !== null && isNum(min) && dom === '*' && dow === '*')
    return { ...fallback, intervalUnit: 'hours', intervalValue: hrStep, intervalMinute: Number(min) };

  return fallback;
}

export function buildCron(s: ScheduleState): string {
  switch (s.frequency) {
    case 'every_15_min':
      return '*/15 * * * *';
    case 'every_30_min':
      return '*/30 * * * *';
    case 'hourly':
      return '0 * * * *';
    case 'daily':
      return `${s.minute} ${s.hour} * * *`;
    case 'weekly':
      return `${s.minute} ${s.hour} * * ${s.dayOfWeek}`;
    case 'monthly':
      return `${s.minute} ${s.hour} ${s.dayOfMonth} * *`;
    case 'custom':
    default:
      return s.intervalUnit === 'minutes'
        ? `*/${s.intervalValue} * * * *`
        : `${s.intervalMinute} */${s.intervalValue} * * *`;
  }
}

// Common timezones
const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Kolkata',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
];

const DAY_LABEL: Record<string, string> = {
  '0': 'Sunday',
  '1': 'Monday',
  '2': 'Tuesday',
  '3': 'Wednesday',
  '4': 'Thursday',
  '5': 'Friday',
  '6': 'Saturday',
};

function formatClock(hour: number, minute: number): string {
  const suffix = hour < 12 ? 'AM' : 'PM';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${pad2(minute)} ${suffix}`;
}

function ordinal(n: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`;
}

// Plain-English schedule label. The raw cron expression is deliberately never
// returned — an expression this can't describe reads as "Custom schedule"
// rather than leaking `*/7 3-6 * * 2` into the UI.
export function describeCron(cron: string): string {
  const parts = (cron || '').trim().split(/\s+/);
  if (parts.length !== 5) return 'Custom schedule';
  const [min, hr, dom, mon, dow] = parts;
  if (mon !== '*') return 'Custom schedule';

  const minStep = asStep(min);
  if (minStep !== null && hr === '*' && dom === '*' && dow === '*')
    return minStep === 1 ? 'Every minute' : `Every ${minStep} minutes`;

  if (isNum(min) && hr === '*' && dom === '*' && dow === '*')
    return Number(min) === 0 ? 'Every hour' : `Every hour at :${pad2(Number(min))}`;

  const hrStep = asStep(hr);
  if (hrStep !== null && isNum(min) && dom === '*' && dow === '*') {
    const past = Number(min) === 0 ? '' : ` at :${pad2(Number(min))} past`;
    return hrStep === 1 ? `Every hour${past}` : `Every ${hrStep} hours${past}`;
  }

  // Clock-based forms. Range-guard before formatting so a malformed expression
  // can't render as "99:00 PM" — the backend would reject it, but the UI still
  // shouldn't claim to understand it.
  if (isNum(min) && isNum(hr) && Number(hr) <= 23 && Number(min) <= 59) {
    const at = formatClock(Number(hr), Number(min));
    if (dom === '*' && dow === '*') return `Every day at ${at}`;
    if (dom === '*' && isNum(dow) && DAY_LABEL[dow]) return `Every ${DAY_LABEL[dow]} at ${at}`;
    if (isNum(dom) && dow === '*' && Number(dom) >= 1 && Number(dom) <= 31)
      return `Monthly on the ${ordinal(Number(dom))} at ${at}`;
  }

  return 'Custom schedule';
}

// Most-recently-updated first.
//
// NOTE: `ScheduledJob.updatedAt` is Prisma `@updatedAt`, and the scheduler
// writes to the job row on every execution (`claimJob` + `updateRunStatus`), so
// this orders by "last touched" — a frequently-running job floats to the top on
// its own, without anyone editing it.
//
// Stable + NaN-safe: equal or unparseable timestamps keep their original
// (server) position rather than shuffling between renders.
export function sortJobsByUpdatedAt<T extends { updatedAt?: string; createdAt?: string }>(
  jobs: T[],
): T[] {
  const at = (job: T) => {
    const parsed = Date.parse(job.updatedAt || job.createdAt || '');
    return Number.isNaN(parsed) ? -Infinity : parsed;
  };
  return jobs
    .map((job, index) => ({ job, index }))
    .sort((a, b) => at(b.job) - at(a.job) || a.index - b.index)
    .map((entry) => entry.job);
}

function getErrorMessage(error: any): string {
  const data = error?.response?.data;
  if (data?.message) {
    return Array.isArray(data.message) ? data.message.join(', ') : data.message;
  }
  return error?.message || 'An unexpected error occurred';
}

function formatDateTime(dateStr?: string): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
}

function timeAgo(dateStr?: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function ScheduledJobs() {
  const toast = useToast();
  const [jobs, setJobs] = useState<ScheduledJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<ScheduledJob | null>(null);
  // Snapshot of the job's values when the edit modal opened, so we can disable
  // "Save Job" until something actually changes.
  const [originalFormData, setOriginalFormData] = useState<CreateScheduledJobDto | null>(null);
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  // #20582 — the server's rejection reason (e.g. "Only TIME_BASED rules can be
  // run as a scheduled job"), shown inline in the create dialog so a failed
  // create is never silent even if the toast is missed.
  const [createError, setCreateError] = useState<string | null>(null);
  const [stats, setStats] = useState<JobStats | null>(null);
  const [runningJobId, setRunningJobId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateScheduledJobDto>({
    name: '',
    description: '',
    cronExpression: '0 * * * *',
    timezone: 'UTC',
    jobType: ScheduledJobType.AUTOMATION_RULE,
    jobConfig: getDefaultJobConfig(ScheduledJobType.AUTOMATION_RULE),
    isActive: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [jobsResponse, statsResponse] = await Promise.all([
        workflowService.getJobs({ limit: 50 }),
        workflowService.getJobStats(),
      ]);
      // The backend stores jobType / lastRunStatus in UPPERCASE (Prisma enums),
      // but this UI keys everything off the lowercase enum values (dropdown
      // options, JOB_TYPE_LABELS, STATUS_COLORS). Normalize on read so the edit
      // modal preselects the right type (instead of defaulting to the first
      // option) and status badges render correctly.
      const normalizedJobs = (jobsResponse.data || []).map((j) => ({
        ...j,
        jobType: (typeof j.jobType === 'string'
          ? j.jobType.toLowerCase()
          : j.jobType) as ScheduledJobType,
        lastRunStatus: j.lastRunStatus ? j.lastRunStatus.toLowerCase() : j.lastRunStatus,
      }));
      setJobs(normalizedJobs);
      setStats(statsResponse);
    } catch {
      toast.error('Error', 'Failed to load scheduled jobs');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (jobId: string) => {
    const newExpanded = new Set(expandedJobs);
    if (newExpanded.has(jobId)) {
      newExpanded.delete(jobId);
    } else {
      newExpanded.add(jobId);
    }
    setExpandedJobs(newExpanded);
  };

  const handleToggleJob = async (job: ScheduledJob) => {
    try {
      await workflowService.toggleJob(job.id, !job.isActive);
      toast.success('Success', `Job ${job.isActive ? 'disabled' : 'enabled'}`);
      loadData();
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    }
  };

  const handleDeleteJob = async (job: ScheduledJob) => {
    if (!confirm(`Are you sure you want to delete "${job.name}"?`)) return;
    try {
      await workflowService.deleteJob(job.id);
      toast.success('Success', 'Job deleted');
      loadData();
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    }
  };

  const handleRunNow = async (job: ScheduledJob) => {
    try {
      setRunningJobId(job.id);
      const result = await workflowService.runJobNow(job.id);
      if (result.success) {
        // Prefer the executor's summary (e.g. "3 of 5 stale ticket(s) affected",
        // "12 notification(s) deleted") so the user sees the real impact; fall
        // back to a generic message for job types that don't report a count.
        toast.success(
          'Success',
          result.message
            ? `Job "${job.name}": ${result.message}`
            : `Job "${job.name}" executed successfully`
        );
      } else {
        toast.error('Failed', `Job execution failed: ${result.message || result.status}`);
      }
      loadData();
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    } finally {
      setRunningJobId(null);
    }
  };

  const handleCreateJob = async () => {
    setCreateError(null);
    if (!formData.name) {
      toast.error('Validation Error', 'Name is required');
      return;
    }
    if (!formData.cronExpression) {
      toast.error('Validation Error', 'Schedule is required');
      return;
    }
    const missing = getMissingJobConfigFields(formData.jobType, formData.jobConfig);
    if (missing.length > 0) {
      toast.error('Validation Error', `Missing required config: ${missing.join(', ')}`);
      return;
    }

    try {
      setSaving(true);
      await workflowService.createJob(formData);
      toast.success('Success', 'Scheduled job created');
      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (error: any) {
      const msg = getErrorMessage(error);
      setCreateError(msg);
      toast.error('Error', msg);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateJob = async () => {
    if (!selectedJob) return;
    if (!formData.name) {
      toast.error('Validation Error', 'Name is required');
      return;
    }
    if (!formData.cronExpression) {
      toast.error('Validation Error', 'Schedule is required');
      return;
    }
    const missing = getMissingJobConfigFields(formData.jobType, formData.jobConfig);
    if (missing.length > 0) {
      toast.error('Validation Error', `Missing required config: ${missing.join(', ')}`);
      return;
    }
    try {
      setSaving(true);
      await workflowService.updateJob(selectedJob.id, formData);
      toast.success('Success', 'Scheduled job updated');
      setShowEditModal(false);
      loadData();
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (job: ScheduledJob) => {
    setSelectedJob(job);
    const snapshot: CreateScheduledJobDto = {
      name: job.name,
      description: job.description || '',
      cronExpression: job.cronExpression,
      timezone: job.timezone,
      jobType: job.jobType,
      jobConfig: job.jobConfig,
      isActive: job.isActive,
    };
    setFormData(snapshot);
    setOriginalFormData(snapshot);
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      cronExpression: '0 * * * *',
      timezone: 'UTC',
      jobType: ScheduledJobType.AUTOMATION_RULE,
      jobConfig: getDefaultJobConfig(ScheduledJobType.AUTOMATION_RULE),
      isActive: true,
    });
  };

  const filteredJobs = sortJobsByUpdatedAt(
    jobs.filter(
      (job) =>
        job.name.toLowerCase().includes(search.toLowerCase()) ||
        job.description?.toLowerCase().includes(search.toLowerCase())
    )
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Scheduled Jobs
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Run automations automatically on a repeating schedule
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Job
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Jobs</p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-white">
              {stats.total}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Active Jobs</p>
            <p className="text-2xl font-semibold text-green-600 dark:text-green-400">
              {stats.active}
            </p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
        <Input
          placeholder="Search jobs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Job List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Timer className="w-12 h-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No scheduled jobs
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Create your first scheduled job to run automations on a repeating schedule
          </p>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Job
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredJobs.map((job) => (
            <div
              key={job.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Job Header */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={() => toggleExpand(job.id)}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      job.isActive ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                  />
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      {job.name}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {JOB_TYPE_LABELS[job.jobType] || job.jobType} • {describeCron(job.cronExpression)}
                      {job.nextRunAt && job.isActive && (
                        <span className="ml-2 text-gray-400 dark:text-gray-500">
                          Next: {formatDateTime(job.nextRunAt)}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleJob(job);
                    }}
                    className={`p-1 ${
                      job.isActive
                        ? 'text-green-600 hover:text-green-700'
                        : 'text-gray-400 hover:text-gray-600 dark:text-gray-500'
                    }`}
                    title={job.isActive ? 'Disable' : 'Enable'}
                  >
                    {job.isActive ? (
                      <Power className="w-4 h-4" />
                    ) : (
                      <PowerOff className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(job);
                    }}
                    className="p-1 text-gray-400 hover:text-blue-600 dark:text-gray-500"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRunNow(job);
                    }}
                    className="p-1 text-gray-400 hover:text-green-600 dark:text-gray-500"
                    title="Run Now"
                    disabled={runningJobId === job.id}
                  >
                    {runningJobId === job.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteJob(job);
                    }}
                    className="p-1 text-gray-400 hover:text-red-600 dark:text-gray-500"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {expandedJobs.has(job.id) ? (
                    <ChevronUp className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {expandedJobs.has(job.id) && (
                <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-700">
                  {job.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {job.description}
                    </p>
                  )}

                  <div className="grid grid-cols-2 gap-4 mb-3">
                    {/* Schedule */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Schedule
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {describeCron(job.cronExpression)}
                      </p>
                      <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
                        Timezone: {job.timezone}
                      </p>
                    </div>

                    {/* Run Info */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Execution
                      </p>
                      {job.lastRunAt ? (
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          <span>Last run: {formatDateTime(job.lastRunAt)}</span>
                          <span className="text-gray-400 ml-1 dark:text-gray-500">({timeAgo(job.lastRunAt)})</span>
                          {job.lastRunStatus && (
                            <span
                              className={`inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 text-xs font-medium rounded-full ${
                                STATUS_COLORS[job.lastRunStatus] || STATUS_COLORS.pending
                              }`}
                            >
                              {STATUS_ICONS[job.lastRunStatus]}
                              {job.lastRunStatus}
                            </span>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 dark:text-gray-500">Never run</p>
                      )}
                      {job.nextRunAt && job.isActive && (
                        <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
                          Next run: {formatDateTime(job.nextRunAt)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Job Config */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Configuration
                    </p>
                    <div className="text-xs bg-gray-50 dark:bg-gray-700 rounded px-2 py-1.5 text-gray-600 dark:text-gray-400 font-mono whitespace-pre-wrap">
                      {JSON.stringify(job.jobConfig, null, 2)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <JobModal
          title="Create Scheduled Job"
          formData={formData}
          setFormData={setFormData}
          error={createError}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
            setCreateError(null);
          }}
          onSave={handleCreateJob}
          saving={saving}
          submitLabel="Create Job"
          isCreate
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedJob && (
        <JobModal
          title="Edit Scheduled Job"
          formData={formData}
          setFormData={setFormData}
          onClose={() => setShowEditModal(false)}
          onSave={handleUpdateJob}
          saving={saving}
          submitLabel="Save Job"
          submitDisabled={JSON.stringify(formData) === JSON.stringify(originalFormData)}
        />
      )}
    </div>
  );
}

// Friendly schedule picker: frequency dropdown + only the fields that matter,
// building the cron expression behind the scenes. No cron expression is ever
// shown — "Custom" is an interval builder ("run every N minutes/hours"), not a
// raw text box.
function SchedulePicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (cron: string) => void;
}) {
  const initial = parseCron(value);
  const [frequency, setFrequency] = useState<ScheduleFrequency>(initial.frequency);
  const [hour, setHour] = useState(initial.hour);
  const [minute, setMinute] = useState(initial.minute);
  const [dayOfWeek, setDayOfWeek] = useState(initial.dayOfWeek);
  const [dayOfMonth, setDayOfMonth] = useState(initial.dayOfMonth);
  const [intervalValue, setIntervalValue] = useState(initial.intervalValue);
  const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>(initial.intervalUnit);
  const [intervalMinute, setIntervalMinute] = useState(initial.intervalMinute);
  const didMount = useRef(false);

  // Emit the rebuilt cron on user changes (skip the initial mount so we don't
  // overwrite the existing value or mark the edit form dirty on open).
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    onChange(
      buildCron({
        frequency,
        hour,
        minute,
        dayOfWeek,
        dayOfMonth,
        intervalValue,
        intervalUnit,
        intervalMinute,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequency, hour, minute, dayOfWeek, dayOfMonth, intervalValue, intervalUnit, intervalMinute]);

  const changeUnit = (unit: IntervalUnit) => {
    setIntervalUnit(unit);
    // The current value usually isn't offered by the other unit (30 minutes has
    // no hours equivalent), so fall back to that unit's default.
    if (!INTERVAL_OPTIONS[unit].includes(intervalValue)) setIntervalValue(DEFAULT_INTERVAL[unit]);
  };

  const fieldClass =
    'rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white';
  const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  const needsTime = frequency === 'daily' || frequency === 'weekly' || frequency === 'monthly';
  const currentCron = buildCron({
    frequency,
    hour,
    minute,
    dayOfWeek,
    dayOfMonth,
    intervalValue,
    intervalUnit,
    intervalMinute,
  });

  // Keep a value loaded from an existing job selectable even when it isn't one
  // of the curated options, so opening an older job can't silently change it.
  const withCurrent = (options: number[], current: number) =>
    options.includes(current) ? options : [...options, current].sort((a, b) => a - b);
  const intervalOptions = withCurrent(INTERVAL_OPTIONS[intervalUnit], intervalValue);
  const minutePastOptions = withCurrent(MINUTE_PAST_OPTIONS, intervalMinute);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <ThemedSelect
          value={frequency}
          onChange={(e) => setFrequency(e.target.value as ScheduleFrequency)}
          className={`${fieldClass} flex-1 min-w-[160px]`}
        >
          <option value="every_15_min">Every 15 minutes</option>
          <option value="every_30_min">Every 30 minutes</option>
          <option value="hourly">Every hour</option>
          <option value="daily">Every day</option>
          <option value="weekly">Every week</option>
          <option value="monthly">Every month</option>
          <option value="custom">Custom interval</option>
        </ThemedSelect>

        {frequency === 'weekly' && (
          <ThemedSelect
            value={dayOfWeek}
            onChange={(e) => setDayOfWeek(e.target.value)}
            className={fieldClass}
          >
            {DAY_OF_WEEK_OPTIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </ThemedSelect>
        )}

        {frequency === 'monthly' && (
          <ThemedSelect
            value={dayOfMonth}
            onChange={(e) => setDayOfMonth(Number(e.target.value))}
            className={fieldClass}
          >
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>
                Day {d}
              </option>
            ))}
          </ThemedSelect>
        )}

        {needsTime && (
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-gray-500 dark:text-gray-400">at</span>
            <input
              type="time"
              value={timeStr}
              onChange={(e) => {
                const [h, m] = e.target.value.split(':').map(Number);
                setHour(Number.isFinite(h) ? h : 0);
                setMinute(Number.isFinite(m) ? m : 0);
              }}
              className={fieldClass}
            />
          </div>
        )}
      </div>

      {frequency === 'custom' && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm text-gray-500 dark:text-gray-400">Run every</span>
          <ThemedSelect
            value={intervalValue}
            onChange={(e) => setIntervalValue(Number(e.target.value))}
            className={fieldClass}
            aria-label="Interval amount"
          >
            {intervalOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </ThemedSelect>
          <ThemedSelect
            value={intervalUnit}
            onChange={(e) => changeUnit(e.target.value as IntervalUnit)}
            className={fieldClass}
            aria-label="Interval unit"
          >
            <option value="minutes">minutes</option>
            <option value="hours">hours</option>
          </ThemedSelect>
          {/* A start minute only means something for an hourly interval — a
              step-minutes schedule already starts at :00. */}
          {intervalUnit === 'hours' && (
            <>
              <span className="text-sm text-gray-500 dark:text-gray-400">at</span>
              <ThemedSelect
                value={intervalMinute}
                onChange={(e) => setIntervalMinute(Number(e.target.value))}
                className={fieldClass}
                aria-label="Minutes past the hour"
              >
                {minutePastOptions.map((m) => (
                  <option key={m} value={m}>
                    :{pad2(m)}
                  </option>
                ))}
              </ThemedSelect>
              <span className="text-sm text-gray-500 dark:text-gray-400">past the hour</span>
            </>
          )}
        </div>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400">{describeCron(currentCron)}</p>
    </div>
  );
}

// Modal component for create/edit
function JobModal({
  title,
  formData,
  setFormData,
  onClose,
  onSave,
  saving,
  submitLabel,
  submitDisabled,
  isCreate,
  error,
}: {
  title: string;
  formData: CreateScheduledJobDto;
  setFormData: (data: CreateScheduledJobDto) => void;
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
  submitLabel: string;
  submitDisabled?: boolean;
  // Server-side rejection reason, shown inline so a failed save is never silent.
  error?: string | null;
  // Create-only affordances (e.g. Report Generation pre-selecting the signed-in
  // admin as a recipient). Never set on edit — see JobConfigPanelProps.isCreate.
  isCreate?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 dark:text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
            >
              {error}
            </div>
          )}
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Daily SLA compliance check"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="What does this scheduled job do?"
                rows={2}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Schedule *
            </label>
            <SchedulePicker
              value={formData.cronExpression}
              onChange={(cron) => setFormData({ ...formData, cronExpression: cron })}
            />
          </div>

          {/* Timezone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Timezone
            </label>
            <ThemedSelect
              value={formData.timezone || 'UTC'}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </ThemedSelect>
          </div>

          {/* Job Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Job Type *
            </label>
            <ThemedSelect
              value={formData.jobType}
              onChange={(e) => {
                const newType = e.target.value as ScheduledJobType;
                setFormData({
                  ...formData,
                  jobType: newType,
                  jobConfig: getDefaultJobConfig(newType),
                });
              }}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            >
              {CREATABLE_JOB_TYPES.map((value) => (
                <option key={value} value={value}>
                  {JOB_TYPE_LABELS[value]}
                </option>
              ))}
            </ThemedSelect>
          </div>

          {/* Job Config */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Configuration
            </label>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
              <JobConfigPanel
                jobType={formData.jobType}
                config={formData.jobConfig}
                onChange={(config) => setFormData({ ...formData, jobConfig: config })}
                isCreate={isCreate}
              />
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isActive !== false}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="rounded border-gray-300 dark:border-gray-700"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving || submitDisabled}>
            {saving ? 'Saving...' : submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
